import { useEffect, useRef, useState } from 'react';
import { springOf } from '@/lib/library/cornerFan';

/**
 * A value that springs toward `target` (work in 0..100 or pixels: the snap
 * threshold is absolute). Frame-rate independent: `dt` is in sixtieths of a
 * second and the decay is raised to it, so a dropped frame loses the same
 * energy as the two it replaced. The loop parks itself once settled.
 */
export function useFanSpring(target: number, tune = 20, instant = false): number {
  const [at, setAt] = useState(target);
  const cur = useRef(target);
  const vel = useRef(0);
  const raf = useRef(0);

  useEffect(() => {
    if (instant) {
      cur.current = target;
      vel.current = 0;
      setAt(target);
      return;
    }
    const { k, d } = springOf(tune);
    let prev = 0;
    const tick = (t: number) => {
      const dt = prev ? Math.min(2.5, Math.max(0, (t - prev) / 16.67)) : 1;
      prev = t;
      vel.current += (target - cur.current) * k * dt;
      vel.current *= Math.pow(d, dt);
      cur.current += vel.current * dt;
      if (Math.abs(target - cur.current) < 0.02 && Math.abs(vel.current) < 0.02) {
        cur.current = target;
        vel.current = 0;
        setAt(target);
        raf.current = 0;
        return;
      }
      setAt(cur.current);
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf.current);
      raf.current = 0;
    };
  }, [target, tune, instant]);

  return at;
}
